import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { DataTablePageSkeleton } from '../components/common/Skeleton';
import { PageFadeSection } from '../components/common/PageFadeSection';
import {
  getUsers,
  getUserStats,
  UserRole,
  ApprovalStatus
} from '../api/adminApi';
import api from '../api/axios';
import { useTheme } from '../context/ThemeContext';

const UserManagementPage = () => {
  const { darkMode } = useTheme();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const searchInputRef = useRef(null);

  // SAP promotion/demotion tracking
  const [showSapConfirm, setShowSapConfirm] = useState(false);
  const [showSapRemovalConfirm, setShowSapRemovalConfirm] = useState(false);
  const [pendingSapData, setPendingSapData] = useState(null);

  // Edit form state
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    department: '',
    isActive: true,
    role: UserRole.User,
    approvalStatus: ApprovalStatus.Pending,
    sapEmployeeId: '',
    sapEmployeeUUID: '',
    rejectionReason: ''
  });

  // Store original data when editing starts
  const [originalData, setOriginalData] = useState({
    sapEmployeeId: '',
    role: UserRole.User
  });

  // Filter states
  const [filters, setFilters] = useState({
    searchTerm: '',
    role: '',
    approvalStatus: '',
    department: '',
    hasSapAccess: '',
    isActive: '',
    page: 1,
    pageSize: 10
  });

  // Pagination states
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
    hasPreviousPage: false,
    hasNextPage: false
  });

  const [departments, setDepartments] = useState(['Engineering', 'Finance', 'HR', 'IT', 'Operations']);
  const [statsLoading, setStatsLoading] = useState(false);
  const [searchDebounce, setSearchDebounce] = useState(null);
  const [localSearchTerm, setLocalSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const usersResult = await getUsers(filters);

      if (usersResult.isSuccess) {
        setUsers(usersResult.data?.items || []);
        setPagination({
          page: usersResult.data?.page || 1,
          pageSize: usersResult.data?.pageSize || 20,
          totalCount: usersResult.data?.totalCount || 0,
          totalPages: usersResult.data?.totalPages || 0,
          hasPreviousPage: usersResult.data?.hasPreviousPage || false,
          hasNextPage: usersResult.data?.hasNextPage || false
        });
      } else {
        toast.error(usersResult.message || 'Failed to load users');
      }

      loadStats();
    } catch (err) {
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.errors?.join(', ') ||
        err.message ||
        'Failed to load data';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const statsResult = await getUserStats();

      if (statsResult.isSuccess) {
        const mappedStats = {
          totalUsers: statsResult.data?.totalUsers || 0,
          pendingApproval: statsResult.data?.pendingApprovals || 0,
          approved: statsResult.data?.approved || 0,
          rejected: statsResult.data?.rejected || 0,
          withSapId: statsResult.data?.sapId || 0,
          activeEmployees: statsResult.data?.activeEmployees || 0,
          totalLeads: statsResult.data?.totalLeads || 0,
          totalAdmins: statsResult.data?.totalAdmins || 0,
        };
        setStats(mappedStats);
      } else {
        toast.error(statsResult.message || 'Failed to load statistics');
      }
    } catch (err) {
      console.error('Error loading stats:', err);
      const calculatedStats = {
        totalUsers: pagination.totalCount || 0,
        pendingApproval: users.filter(u => u.approvalStatus === 0 || u.approvalStatusName === 'Pending').length,
        approved: users.filter(u => u.approvalStatus === 1 || u.approvalStatusName === 'Approved').length,
        rejected: users.filter(u => u.approvalStatus === 2 || u.approvalStatusName === 'Rejected').length,
        withSapId: users.filter(u => u.sapEmployeeId).length,
        activeEmployees: users.filter(u => u.isActive && u.role === UserRole.Employee).length,
        totalLeads: users.filter(u => u.role === UserRole.Lead).length,
        totalAdmins: users.filter(u => u.role === UserRole.Admin).length,
      };
      setStats(calculatedStats);
    } finally {
      setStatsLoading(false);
    }
  };

  const calculateStats = () => {
    if (stats) return stats;

    return {
      totalUsers: pagination.totalCount || 0,
      pendingApproval: users.filter(u => u.approvalStatus === 0 || u.approvalStatusName === 'Pending').length,
      approved: users.filter(u => u.approvalStatus === 1 || u.approvalStatusName === 'Approved').length,
      rejected: users.filter(u => u.approvalStatus === 2 || u.approvalStatusName === 'Rejected').length,
      withSapId: users.filter(u => u.sapEmployeeId).length,
      activeEmployees: users.filter(u => u.isActive && u.role === UserRole.Employee).length,
      totalLeads: users.filter(u => u.role === UserRole.Lead).length,
      totalAdmins: users.filter(u => u.role === UserRole.Admin).length,
    };
  };

  const handleFilterChange = (key, value) => {
    if (key === 'searchTerm') {
      setLocalSearchTerm(value);

      if (searchDebounce) {
        clearTimeout(searchDebounce);
      }

      const timeout = setTimeout(() => {
        setFilters(prev => ({
          ...prev,
          searchTerm: value,
          page: 1
        }));

        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
          }
        }, 0);
      }, 500);

      setSearchDebounce(timeout);
    } else {
      setFilters(prev => ({
        ...prev,
        [key]: value,
        page: 1
      }));
    }
  };

  useEffect(() => {
    if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [users]);

  useEffect(() => {
    if (showDetailModal || showEditModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showDetailModal, showEditModal]);

  useEffect(() => {
    setLocalSearchTerm(filters.searchTerm);
  }, [filters.searchTerm]);

  useEffect(() => {
    return () => {
      if (searchDebounce) {
        clearTimeout(searchDebounce);
      }
    };
  }, [searchDebounce]);

  const handleResetFilters = () => {
    setFilters({
      searchTerm: '',
      role: '',
      approvalStatus: '',
      department: '',
      hasSapAccess: '',
      isActive: '',
      page: 1,
      pageSize: 10
    });
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const openUserDetails = (user) => {
    setSelectedUser(user);
    setShowDetailModal(true);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setShowSapConfirm(false);
    setShowSapRemovalConfirm(false);
    setPendingSapData(null);

    if (user.department && !departments.includes(user.department)) {
      setDepartments(prev => [...prev, user.department].sort());
    }

    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      department: user.department || '',
      isActive: user.isActive || true,
      role: user.role || UserRole.User,
      approvalStatus: user.approvalStatus || ApprovalStatus.Pending,
      sapEmployeeId: user.sapEmployeeId || '',
      sapEmployeeUUID: user.sapEmployeeUUID || '',
      rejectionReason: user.rejectionReason || ''
    });

    setOriginalData({
      sapEmployeeId: user.sapEmployeeId || '',
      role: user.role || UserRole.User
    });

    setShowDetailModal(false);
    setShowEditModal(true);
  };

  // Simple edit form change handler
  const handleEditFormChange = (key, value) => {
    setEditForm(prev => ({ ...prev, [key]: value }));
  };

  // Handle update button click - check for SAP promotion or demotion
  const handleUpdateClick = () => {
    const originalRole = originalData.role;
    const currentSapId = editForm.sapEmployeeId?.trim();
    const originalSapId = originalData.sapEmployeeId?.trim();

    // Case 1: User -> Employee (Adding SAP ID)
    if (originalRole === UserRole.User &&
      currentSapId !== '' &&
      currentSapId !== originalSapId) {

      // Show confirmation popup for promotion
      setPendingSapData({
        sapEmployeeId: currentSapId,
        willBecomeRole: UserRole.Employee,
        action: 'promote'
      });
      setShowSapConfirm(true);
    }
    // Case 2: Employee -> User (Removing SAP ID)
    else if (originalRole === UserRole.Employee &&
      currentSapId === '' &&
      originalSapId !== '') {

      // Show confirmation popup for demotion
      setPendingSapData({
        willLoseRole: UserRole.Employee,
        willBecomeRole: UserRole.User,
        action: 'demote'
      });
      setShowSapRemovalConfirm(true);
    }
    else {
      // No SAP change needed, submit directly
      handleEditSubmit(editForm);
    }
  };

  // User confirmed the promotion (User -> Employee)
  const handleSapConfirmOk = () => {
    setShowSapConfirm(false);

    // Update the form role to Employee immediately
    const updatedForm = {
      ...editForm,
      role: UserRole.Employee
    };

    setEditForm(updatedForm);

    // toast.success('Promoting user to Employee', { duration: 3000 });

    // Submit the form with the updated role
    handleEditSubmit(updatedForm);
  };

  // User rejected the promotion
  const handleSapConfirmNo = () => {
    setShowSapConfirm(false);
    setPendingSapData(null);

    // Clear the SAP ID from form and keep role as User
    const updatedForm = {
      ...editForm,
      sapEmployeeId: '',
      role: UserRole.User
    };

    setEditForm(updatedForm);

    // toast.success('SAP ID cleared, role remains as User', { duration: 3000 });

    // Submit the form with cleared SAP ID
    handleEditSubmit(updatedForm);
  };

  // User confirmed the demotion (Employee -> User)
  const handleSapRemovalConfirmOk = () => {
    setShowSapRemovalConfirm(false);

    // Update the form role to User immediately
    const updatedForm = {
      ...editForm,
      role: UserRole.User
    };

    setEditForm(updatedForm);

    // toast.success('User demoted to regular User role', { duration: 3000 });

    // Submit the form with the updated role
    handleEditSubmit(updatedForm);
  };

  // User rejected the demotion - keep as Employee
  const handleSapRemovalConfirmNo = () => {
    setShowSapRemovalConfirm(false);
    setPendingSapData(null);

    // Restore the original SAP ID and keep role as Employee
    const updatedForm = {
      ...editForm,
      sapEmployeeId: originalData.sapEmployeeId,
      role: UserRole.Employee
    };

    setEditForm(updatedForm);

    // toast.success('User remains as Employee', { duration: 3000 });

    // Submit the form with restored SAP ID
    handleEditSubmit(updatedForm);
  };

  // Main submit function that accepts form data
  const handleEditSubmit = async (formDataToSubmit) => {
    if (!selectedUser) return;

    const updatePromise = new Promise(async (resolve, reject) => {
      try {
        setEditLoading(true);
        setError(null);

        const requestBody = {
          firstName: formDataToSubmit.firstName.trim(),
          lastName: formDataToSubmit.lastName.trim(),
          department: formDataToSubmit.department.trim(),
          isActive: formDataToSubmit.isActive,
          role: formDataToSubmit.role,
          approvalStatus: formDataToSubmit.approvalStatus,
          sapEmployeeId: formDataToSubmit.sapEmployeeId.trim(),
          sapEmployeeUUID: formDataToSubmit.sapEmployeeUUID.trim(),
          rejectionReason: formDataToSubmit.rejectionReason.trim()
        };

        const response = await api.put(`/api/auth/${selectedUser.id}`, requestBody);

        if (response.data.isSuccess) {
          const getRoleName = (roleValue) => {
            const roleMap = { 0: 'User', 1: 'Employee', 2: 'Lead', 3: 'PM', 4: 'Admin' };
            return roleMap[roleValue] || 'Unknown';
          };

          const getApprovalStatusName = (statusValue) => {
            const statusMap = { 0: 'Pending', 1: 'Approved', 2: 'Rejected' };
            return statusMap[statusValue] || 'Unknown';
          };

          setUsers(prevUsers =>
            prevUsers.map(user =>
              user.id === selectedUser.id
                ? {
                  ...user,
                  ...response.data.data,
                  fullName: `${formDataToSubmit.firstName} ${formDataToSubmit.lastName}`,
                  firstName: formDataToSubmit.firstName,
                  lastName: formDataToSubmit.lastName,
                  department: formDataToSubmit.department,
                  isActive: formDataToSubmit.isActive,
                  role: formDataToSubmit.role,
                  roleName: getRoleName(formDataToSubmit.role),
                  approvalStatus: formDataToSubmit.approvalStatus,
                  approvalStatusName: getApprovalStatusName(formDataToSubmit.approvalStatus),
                  sapEmployeeId: formDataToSubmit.sapEmployeeId,
                  sapEmployeeUUID: formDataToSubmit.sapEmployeeUUID,
                  rejectionReason: formDataToSubmit.rejectionReason
                }
                : user
            )
          );

          loadStats();
          setShowEditModal(false);
          setSelectedUser(null);
          resolve('User updated successfully!');
        } else {
          reject(new Error(response.data.message || 'Failed to update user'));
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message ||
          err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to update user';
        reject(new Error(errorMessage));
        console.error('Error updating user:', err);
      } finally {
        setEditLoading(false);
      }
    });

    toast.promise(
      updatePromise,
      {
        loading: 'Updating user...',
        success: (message) => message,
        error: (err) => err.message || 'Failed to update user',
      },
      {
        style: { minWidth: '250px' },
        success: { duration: 4000, icon: '✅' },
        error: { duration: 5000, icon: '❌' },
      }
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved': return 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800 border border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800';
      case 'Rejected': return 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800';
      default: return 'bg-gray-100 text-gray-800 border border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'Admin': return 'bg-purple-100 text-purple-800 border border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800';
      case 'Lead': return 'bg-blue-100 text-blue-800 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800';
      case 'PM': return 'bg-indigo-100 text-indigo-800 border border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-800';
      case 'Employee': return 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800';
      default: return 'bg-gray-100 text-gray-800 border border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600';
    }
  };

  const displayStats = calculateStats();

  const renderStatsCards = () => (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
      {/* Total Users */}
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-lg p-4 border border-blue-200 dark:border-blue-800 hover:shadow-md transition-all duration-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wide">Total Users</div>
            <div className="text-2xl font-bold text-blue-900 dark:text-blue-200 mt-1">
              {statsLoading ? (
                <div className="h-7 w-20 bg-blue-200 dark:bg-blue-700 animate-pulse rounded"></div>
              ) : (
                displayStats.totalUsers?.toLocaleString() || '0'
              )}
            </div>
          </div>
          <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
            <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5 0c-.966.028-1.93.08-2.888.156A12.01 12.01 0 0112 8.5m0 0a12 12 0 00-8.5 3.5M12 8.5a12 12 0 018.5 3.5" />
            </svg>
          </div>
        </div>
        <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">All registered accounts</div>
      </div>

      {/* Pending */}
      <div className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/30 dark:to-amber-800/30 rounded-lg p-4 border border-amber-200 dark:border-amber-800 hover:shadow-md transition-all duration-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wide">Pending</div>
            <div className="text-2xl font-bold text-amber-900 dark:text-amber-200 mt-1">
              {statsLoading ? (
                <div className="h-7 w-20 bg-amber-200 dark:bg-amber-700 animate-pulse rounded"></div>
              ) : (
                displayStats.pendingApproval?.toLocaleString() || '0'
              )}
            </div>
          </div>
          <div className="h-10 w-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
            <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
        <div className="mt-2 text-xs text-amber-600 dark:text-amber-400">Awaiting approval</div>
      </div>

      {/* Approved */}
      <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/30 dark:to-emerald-800/30 rounded-lg p-4 border border-emerald-200 dark:border-emerald-800 hover:shadow-md transition-all duration-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wide">Approved</div>
            <div className="text-2xl font-bold text-emerald-900 dark:text-emerald-200 mt-1">
              {statsLoading ? (
                <div className="h-7 w-20 bg-emerald-200 dark:bg-emerald-700 animate-pulse rounded"></div>
              ) : (
                displayStats.approved?.toLocaleString() || '0'
              )}
            </div>
          </div>
          <div className="h-10 w-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
            <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
        <div className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">Verified accounts</div>
      </div>

      {/* Rejected */}
      <div className="bg-gradient-to-br from-rose-50 to-rose-100 dark:from-rose-900/30 dark:to-rose-800/30 rounded-lg p-4 border border-rose-200 dark:border-rose-800 hover:shadow-md transition-all duration-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold text-rose-700 dark:text-rose-400 uppercase tracking-wide">Rejected</div>
            <div className="text-2xl font-bold text-rose-900 dark:text-rose-200 mt-1">
              {statsLoading ? (
                <div className="h-7 w-20 bg-rose-200 dark:bg-rose-700 animate-pulse rounded"></div>
              ) : (
                displayStats.rejected?.toLocaleString() || '0'
              )}
            </div>
          </div>
          <div className="h-10 w-10 bg-gradient-to-br from-rose-500 to-rose-600 rounded-lg flex items-center justify-center">
            <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        </div>
        <div className="mt-2 text-xs text-rose-600 dark:text-rose-400">Declined applications</div>
      </div>

      {/* SAP Users */}
      <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/30 dark:to-indigo-800/30 rounded-lg p-4 border border-indigo-200 dark:border-indigo-800 hover:shadow-md transition-all duration-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold text-indigo-700 dark:text-indigo-400 uppercase tracking-wide">SAP Users</div>
            <div className="text-2xl font-bold text-indigo-900 dark:text-indigo-200 mt-1">
              {statsLoading ? (
                <div className="h-7 w-20 bg-indigo-200 dark:bg-indigo-700 animate-pulse rounded"></div>
              ) : (
                displayStats.withSapId?.toLocaleString() || '0'
              )}
            </div>
          </div>
          <div className="h-10 w-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center">
            <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
            </svg>
          </div>
        </div>
        <div className="mt-2 text-xs text-indigo-600 dark:text-indigo-400">SAP integrated accounts</div>
      </div>
    </div>
  );

  const renderUserCard = (user) => (
    <div key={user.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className="flex-shrink-0 h-12 w-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
              <span className="text-blue-600 dark:text-blue-400 font-semibold text-lg">
                {user.firstName?.[0]}{user.lastName?.[0]}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate" title={user.fullName}>
                {user.fullName}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate" title={user.email}>
                {user.email}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end space-y-1 ml-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(user.approvalStatusName)}`}>
              {user.approvalStatusName}
            </span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getRoleColor(user.roleName)}`}>
              {user.roleName}
            </span>
          </div>
        </div>

        <div className="space-y-3 mb-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate capitalize" title={user.department || 'Not specified'}>
                {user.department || 'Not specified'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">SAP Access</p>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${user.hasSapAccess ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}`}>
                {user.hasSapAccess ? 'Yes' : 'No'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${user.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'}`}>
                {user.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">SAP Employee ID</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={user.sapEmployeeId || 'Not assigned'}>
                {user.sapEmployeeId || 'Not assigned'}
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500 dark:text-gray-400">Registered:</span>
              <span className="font-medium text-gray-900 dark:text-gray-200 truncate ml-2" title={formatDate(user.createdAt)}>
                {formatDate(user.createdAt)}
              </span>
            </div>

            {user.approvalStatusName === 'Pending' ? (
              <div className="flex justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-400">Status:</span>
                <span className="font-medium text-yellow-600 dark:text-yellow-400 flex items-center">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  Awaiting Approval
                </span>
              </div>
            ) : user.approvalStatusName === 'Approved' && user.approvalDate ? (
              <div className="flex justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-400">Approved:</span>
                <span className="font-medium text-green-600 dark:text-green-400 truncate ml-2" title={formatDate(user.approvalDate)}>
                  {formatDate(user.approvalDate)}
                </span>
              </div>
            ) : user.approvalStatusName === 'Rejected' && user.rejectionReason ? (
              <div className="flex justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-400">Rejected:</span>
                <span className="font-medium text-red-600 dark:text-red-400">See details</span>
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
          <button
            onClick={() => openUserDetails(user)}
            className="w-full px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 text-sm font-medium transition-colors duration-200"
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  );

  if (loading && !users.length) {
    return <DataTablePageSkeleton />;
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <PageFadeSection>
      {/* Page Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div className="relative mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5 0c-.966.028-1.93.08-2.888.156A12.01 12.01 0 0112 8.5m0 0a12 12 0 00-8.5 3.5M12 8.5a12 12 0 018.5 3.5" />
                </svg>
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  User Management
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                  Manage user accounts, roles, and permissions
                </p>
              </div>
            </div>
          </div>
          <div className="absolute -bottom-2 left-0 h-0.5 w-24 bg-gradient-to-r from-blue-500 to-transparent"></div>
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Showing {users.length} of {pagination.totalCount} users
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4">
          {error}
          <button
            onClick={() => { setError(null); toast.success('Error cleared'); }}
            className="float-right hover:text-red-900 dark:hover:text-red-300"
          >
            &times;
          </button>
        </div>
      )}

      {/* Stats Cards */}
      {renderStatsCards()}
      </PageFadeSection>

      <PageFadeSection>
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 md:mb-0">Filters</h2>
          <button
            onClick={handleResetFilters}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center gap-2"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Reset Filters
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by email..."
              value={localSearchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role</label>
              <select
                value={filters.role}
                onChange={(e) => handleFilterChange('role', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Roles</option>
                <option value={UserRole.Admin} style={{ backgroundColor: darkMode ? '#374151' : 'white' }}>Admin</option>
                <option value={UserRole.Employee} style={{ backgroundColor: darkMode ? '#374151' : 'white' }}>Employee</option>
                <option value={UserRole.Lead} style={{ backgroundColor: darkMode ? '#374151' : 'white' }}>Lead</option>
                <option value={UserRole.PM} style={{ backgroundColor: darkMode ? '#374151' : 'white' }}>PM</option>
                <option value={UserRole.User} style={{ backgroundColor: darkMode ? '#374151' : 'white' }}>User</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Approval Status</label>
              <select
                value={filters.approvalStatus}
                onChange={(e) => handleFilterChange('approvalStatus', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Statuses</option>
                <option value={ApprovalStatus.Approved} style={{ backgroundColor: darkMode ? '#374151' : 'white' }}>Approved</option>
                <option value={ApprovalStatus.Pending} style={{ backgroundColor: darkMode ? '#374151' : 'white' }}>Pending</option>
                <option value={ApprovalStatus.Rejected} style={{ backgroundColor: darkMode ? '#374151' : 'white' }}>Rejected</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Department</label>
              <select
                value={filters.department}
                onChange={(e) => handleFilterChange('department', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Departments</option>
                {departments.map(dept => (
                  <option key={dept} value={dept} style={{ backgroundColor: darkMode ? '#374151' : 'white', color: darkMode ? 'white' : 'inherit' }}>{dept}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">SAP Access</label>
              <select
                value={filters.hasSapAccess}
                onChange={(e) => handleFilterChange('hasSapAccess', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">All</option>
                <option value="true">Has SAP Access</option>
                <option value="false">No SAP Access</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Active Status</label>
              <select
                value={filters.isActive}
                onChange={(e) => handleFilterChange('isActive', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Items per page</label>
              <select
                value={filters.pageSize}
                onChange={(e) => handleFilterChange('pageSize', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          {/* Active Filter Chips */}
          {Object.values(filters).some(value => value && value !== '' && value !== 20) && (
            <div className="flex flex-wrap gap-2 pt-2">
              {Object.entries(filters).map(([key, value]) => {
                if (value && value !== '' && key !== 'page' && key !== 'pageSize') {
                  const getDisplayValue = (key, value) => {
                    if (key === 'role') {
                      const roleMap = { [UserRole.User]: 'User', [UserRole.Employee]: 'Employee', [UserRole.Lead]: 'Lead', [UserRole.PM]: 'PM', [UserRole.Admin]: 'Admin' };
                      return roleMap[value] || value;
                    }
                    if (key === 'approvalStatus') {
                      const statusMap = { [ApprovalStatus.Pending]: 'Pending', [ApprovalStatus.Approved]: 'Approved', [ApprovalStatus.Rejected]: 'Rejected' };
                      return statusMap[value] || value;
                    }
                    if (key === 'hasSapAccess' || key === 'isActive') {
                      return value === 'true' ? 'Yes' : value === 'false' ? 'No' : value;
                    }
                    return value;
                  };

                  const getDisplayLabel = (key) => {
                    const labelMap = { searchTerm: 'Search', role: 'Role', approvalStatus: 'Status', department: 'Department', hasSapAccess: 'SAP Access', isActive: 'Active' };
                    return labelMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                  };

                  return (
                    <span key={key} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                      {getDisplayLabel(key)}: {getDisplayValue(key, value)}
                      <button onClick={() => handleFilterChange(key, '')} className="ml-1 hover:text-blue-600 dark:hover:text-blue-300">
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  );
                }
                return null;
              })}
            </div>
          )}
        </div>
      </div>
      </PageFadeSection>

      <PageFadeSection>
      {/* Users Grid */}
      {users.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5 0c-.966.028-1.93.08-2.888.156A12.01 12.01 0 0112 8.5m0 0a12 12 0 00-8.5 3.5M12 8.5a12 12 0 018.5 3.5" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No users found</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Try adjusting your filters or search term.</p>
          <div className="mt-6">
            <button
              onClick={handleResetFilters}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
            >
              Reset Filters
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {users.map(renderUserCard)}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6 rounded-lg shadow">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.hasPreviousPage}
                  className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.hasNextPage}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing <span className="font-medium">{((pagination.page - 1) * pagination.pageSize) + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(pagination.page * pagination.pageSize, pagination.totalCount)}</span>{' '}
                    of <span className="font-medium">{pagination.totalCount}</span> results
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={!pagination.hasPreviousPage}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      Previous
                    </button>
                    {[...Array(pagination.totalPages)].map((_, i) => (
                      <button
                        key={i + 1}
                        onClick={() => handlePageChange(i + 1)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold transition-colors duration-200 ${pagination.page === i + 1
                          ? 'z-10 bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                          : 'text-gray-900 dark:text-gray-300 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0'
                          }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={!pagination.hasNextPage}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      </PageFadeSection>

      {/* User Detail Modal */}
      {showDetailModal && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity" onClick={() => setShowDetailModal(false)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-4xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden">

              {/* Header */}
              <div className="relative bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="h-14 w-14 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                      <span className="text-white font-bold text-xl">
                        {selectedUser.firstName?.[0]}{selectedUser.lastName?.[0]}
                      </span>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">{selectedUser.fullName}</h2>
                      <p className="text-blue-100 text-base">{selectedUser.email}</p>
                    </div>
                  </div>
                  <button onClick={() => setShowDetailModal(false)} className="text-white/90 hover:text-white p-2 hover:bg-white/20 rounded-lg transition-all duration-200">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
                <div className="p-6 space-y-8">

                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-100 dark:border-blue-800">
                      <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">Role</p>
                      <span className={`inline-flex mt-2 px-3 py-1.5 rounded-full text-sm font-bold ${getRoleColor(selectedUser.roleName)}`}>{selectedUser.roleName}</span>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-100 dark:border-green-800">
                      <p className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wide">Status</p>
                      <span className={`inline-flex mt-2 px-3 py-1.5 rounded-full text-sm font-bold ${getStatusColor(selectedUser.approvalStatusName)}`}>{selectedUser.approvalStatusName}</span>
                    </div>
                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 border border-purple-100 dark:border-purple-800">
                      <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide">Active</p>
                      <span className={`inline-flex mt-2 px-3 py-1.5 rounded-full text-sm font-bold ${selectedUser.isActive ? 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800' : 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800'}`}>
                        {selectedUser.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-100 dark:border-amber-800">
                      <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide">SAP Access</p>
                      <span className={`inline-flex mt-2 px-3 py-1.5 rounded-full text-sm font-bold ${selectedUser.hasSapAccess ? 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800' : 'bg-gray-100 text-gray-800 border border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600'}`}>
                        {selectedUser.hasSapAccess ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>

                  {/* Personal Information */}
                  <div className="space-y-6">
                    <div className="flex items-center space-x-3">
                      <div className="h-6 w-1.5 bg-blue-600 rounded-full"></div>
                      <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200">Personal Information</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex items-center space-x-3 mb-2">
                          <svg className="h-5 w-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                          <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">Full Name</span>
                        </div>
                        <p className="text-gray-800 dark:text-gray-200 font-semibold text-lg ml-8">{selectedUser.fullName}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex items-center space-x-3 mb-2">
                          <svg className="h-5 w-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89-4.26a2 2 0 012.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                          <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">Email Address</span>
                        </div>
                        <p className="text-gray-800 dark:text-gray-200 font-semibold text-lg ml-8">{selectedUser.email}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex items-center space-x-3 mb-2">
                          <svg className="h-5 w-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                          <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">Department</span>
                        </div>
                        <p className="text-gray-800 dark:text-gray-200 font-semibold text-lg ml-8">{selectedUser.department || 'Not specified'}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex items-center space-x-3 mb-2">
                          <svg className="h-5 w-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" /></svg>
                          <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">SAP Employee ID</span>
                        </div>
                        <p className="text-gray-800 dark:text-gray-200 font-semibold text-lg ml-8">{selectedUser.sapEmployeeId || 'Not assigned'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="space-y-6">
                    <div className="flex items-center space-x-3">
                      <div className="h-6 w-1.5 bg-green-600 rounded-full"></div>
                      <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200">Activity Timeline</h3>
                    </div>
                    <div className="relative pl-8">
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-300 dark:bg-gray-600"></div>
                      <div className="space-y-8">
                        <div className="relative">
                          <div className="absolute -left-7 top-0 h-4 w-4 bg-blue-500 rounded-full ring-4 ring-blue-100 dark:ring-blue-900"></div>
                          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-100 dark:border-blue-800">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-blue-700 dark:text-blue-400">Registration</span>
                              <span className="text-xs text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded-full">{formatDate(selectedUser.createdAt)}</span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">User account was created</p>
                          </div>
                        </div>

                        {selectedUser.approvalStatusName === 'Pending' ? (
                          <div className="relative">
                            <div className="absolute -left-7 top-0 h-4 w-4 bg-yellow-500 rounded-full ring-4 ring-yellow-100 dark:ring-yellow-900"></div>
                            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-4 border border-yellow-100 dark:border-yellow-800">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-semibold text-yellow-700 dark:text-yellow-400">Pending Approval</span>
                                <span className="text-xs text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30 px-2 py-1 rounded-full">Awaiting Review</span>
                              </div>
                              <div className="space-y-1">
                                <p className="text-sm text-gray-600 dark:text-gray-400">User account is waiting for approval</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Submitted: {formatDate(selectedUser.createdAt)}</p>
                              </div>
                            </div>
                          </div>
                        ) : selectedUser.approvalStatusName === 'Approved' && selectedUser.approvalDate ? (
                          <div className="relative">
                            <div className="absolute -left-7 top-0 h-4 w-4 bg-purple-500 rounded-full ring-4 ring-purple-100 dark:ring-purple-900"></div>
                            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 border border-purple-100 dark:border-purple-800">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-semibold text-purple-700 dark:text-purple-400">Approval</span>
                                <span className="text-xs text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30 px-2 py-1 rounded-full">{formatDate(selectedUser.approvalDate)}</span>
                              </div>
                              <div className="space-y-1">
                                <p className="text-sm text-gray-600 dark:text-gray-400">User account was approved</p>
                                {selectedUser.approvedByName && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400">Approved by: <span className="font-semibold">{selectedUser.approvedByName}</span></p>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : null}

                        {selectedUser.rejectionReason && (
                          <div className="relative">
                            <div className="absolute -left-7 top-0 h-4 w-4 bg-red-500 rounded-full ring-4 ring-red-100 dark:ring-red-900"></div>
                            <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4 border border-red-100 dark:border-red-800">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-semibold text-red-700 dark:text-red-400">Rejection Reason</span>
                              </div>
                              <div className="p-3 bg-white/50 dark:bg-gray-700/50 rounded-lg">
                                <p className="text-sm text-red-700 dark:text-red-400 font-medium">{selectedUser.rejectionReason}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* System Access */}
                  <div className="space-y-6">
                    <div className="flex items-center space-x-3">
                      <div className="h-6 w-1.5 bg-indigo-600 rounded-full"></div>
                      <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200">System Access</h3>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-xl p-5 border border-gray-200 dark:border-gray-600">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="h-10 w-10 bg-white dark:bg-gray-500 rounded-lg flex items-center justify-center shadow-sm">
                            <svg className="h-6 w-6 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                          </div>
                          <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200">System Access</h4>
                        </div>
                        <div className="flex items-center justify-between bg-white dark:bg-gray-700 p-3 rounded-lg">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Platform Access</span>
                          <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${selectedUser.canAccessSystem ? 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800' : 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800'}`}>
                            {selectedUser.canAccessSystem ? 'ALLOWED' : 'DENIED'}
                          </span>
                        </div>
                      </div>

                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-xl p-5 border border-gray-200 dark:border-gray-600">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="h-10 w-10 bg-white dark:bg-gray-500 rounded-lg flex items-center justify-center shadow-sm">
                            <svg className="h-6 w-6 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                          </div>
                          <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200">SAP Integration</h4>
                        </div>
                        <div className="flex items-center justify-between bg-white dark:bg-gray-700 p-3 rounded-lg">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">SAP System Access</span>
                          <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${selectedUser.hasSapAccess ? 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800' : 'bg-gray-100 text-gray-800 border border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600'}`}>
                            {selectedUser.hasSapAccess ? 'ENABLED' : 'DISABLED'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Member since:</span>{' '}
                  {new Date(selectedUser.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
                <div className="flex space-x-3">
                  <button onClick={() => setShowDetailModal(false)} className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 hover:border-gray-400 transition-all duration-200">
                    Close
                  </button>
                  <button onClick={() => openEditModal(selectedUser)} className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200">
                    Edit User
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity" onClick={() => setShowEditModal(false)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden">

              {/* Header */}
              <div className="relative bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="h-14 w-14 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                      <span className="text-white font-bold text-xl">
                        {selectedUser.firstName?.[0]}{selectedUser.lastName?.[0]}
                      </span>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">Edit User</h2>
                      <p className="text-blue-100 text-base">{selectedUser.email}</p>
                    </div>
                  </div>
                  <button onClick={() => setShowEditModal(false)} className="text-white/90 hover:text-white p-2 hover:bg-white/20 rounded-lg transition-all duration-200">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={(e) => { e.preventDefault(); handleUpdateClick(); }} className="p-6">
                <div className="space-y-6">

                  {/* Name */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">First Name *</label>
                      <input
                        type="text"
                        required
                        value={editForm.firstName}
                        onChange={(e) => handleEditFormChange('firstName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="Enter first name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Last Name *</label>
                      <input
                        type="text"
                        required
                        value={editForm.lastName}
                        onChange={(e) => handleEditFormChange('lastName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="Enter last name"
                      />
                    </div>
                  </div>

                  {/* Department */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Department</label>
                    <select
                      value={editForm.department}
                      onChange={(e) => handleEditFormChange('department', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="" style={{ backgroundColor: darkMode ? '#374151' : 'white', color: darkMode ? 'white' : 'inherit' }}>Select Department</option>
                      {departments.map(dept => (
                        <option key={dept} value={dept} style={{ backgroundColor: darkMode ? '#374151' : 'white', color: darkMode ? 'white' : 'inherit' }}>{dept}</option>
                      ))}
                    </select>
                  </div>

                  {/* Role & Status */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role</label>
                      <select
                        value={editForm.role}
                        onChange={(e) => handleEditFormChange('role', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      >
                        <option value={UserRole.Admin} style={{ backgroundColor: darkMode ? '#374151' : 'white' }}>Admin</option>
                        <option value={UserRole.Employee} style={{ backgroundColor: darkMode ? '#374151' : 'white' }}>Employee</option>
                        <option value={UserRole.Lead} style={{ backgroundColor: darkMode ? '#374151' : 'white' }}>Lead</option>
                        <option value={UserRole.PM} style={{ backgroundColor: darkMode ? '#374151' : 'white' }}>PM</option>
                        <option value={UserRole.User} style={{ backgroundColor: darkMode ? '#374151' : 'white' }}>User</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Approval Status</label>
                      <select
                        value={editForm.approvalStatus}
                        onChange={(e) => handleEditFormChange('approvalStatus', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      >
                        <option value={ApprovalStatus.Approved} style={{ backgroundColor: darkMode ? '#374151' : 'white' }}>Approved</option>
                        <option value={ApprovalStatus.Pending} style={{ backgroundColor: darkMode ? '#374151' : 'white' }}>Pending</option>
                        <option value={ApprovalStatus.Rejected} style={{ backgroundColor: darkMode ? '#374151' : 'white' }}>Rejected</option>
                      </select>
                    </div>
                  </div>

                  {/* SAP Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        SAP Employee ID
                        {originalData.role === UserRole.Employee && (
                          <span className="ml-2 text-xs text-blue-500 font-normal">(Removing this will demote to User)</span>
                        )}
                      </label>
                      <input
                        type="text"
                        value={editForm.sapEmployeeId}
                        onChange={(e) => handleEditFormChange('sapEmployeeId', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="Enter SAP Employee ID"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">SAP Employee UUID</label>
                      <input
                        type="text"
                        value={editForm.sapEmployeeUUID}
                        onChange={(e) => handleEditFormChange('sapEmployeeUUID', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="Enter SAP Employee UUID"
                      />
                    </div>
                  </div>

                  {/* Rejection Reason */}
                  {editForm.approvalStatus === ApprovalStatus.Rejected && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rejection Reason</label>
                      <textarea
                        value={editForm.rejectionReason}
                        onChange={(e) => handleEditFormChange('rejectionReason', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="Enter rejection reason"
                      />
                    </div>
                  )}

                  {/* Active Toggle */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={editForm.isActive}
                      onChange={(e) => handleEditFormChange('isActive', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700"
                    />
                    <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      User is active
                    </label>
                  </div>
                </div>

                {/* Form Footer */}
                <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-600 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    disabled={editLoading}
                    className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 hover:border-gray-400 transition-all duration-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={editLoading}
                    className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {editLoading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Updating...
                      </span>
                    ) : 'Update User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* SAP Promotion Confirmation Dialog (User -> Employee) */}
      {showSapConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowSapConfirm(false)} />

          <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden border-2 border-amber-400 dark:border-amber-500 animate-[fadeInScale_0.15s_ease-out]">

            {/* Warning Header */}
            <div className="bg-gradient-to-r from-amber-400 to-orange-500 px-6 py-4 flex items-center space-x-3">
              <div className="h-11 w-11 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white tracking-tight">Role Change Warning</h3>
                <p className="text-amber-100 text-xs mt-0.5">This action will modify the user's role</p>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 leading-relaxed">
                  ⚠️ Assigning SAP Employee ID{' '}
                  <span className="font-bold bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 rounded text-amber-900 dark:text-amber-200">
                    "{pendingSapData?.sapEmployeeId}"
                  </span>{' '}
                  will automatically promote this user from{' '}
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300 mx-0.5">User</span>
                  {' '}→{' '}
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 mx-0.5">Employee</span>.
                </p>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400">
                Do you want to proceed? Pressing <span className="font-semibold text-gray-800 dark:text-gray-200">"No, Revert"</span> will clear the SAP ID and keep the role as <span className="font-semibold">User</span>.
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 pb-5 flex justify-end space-x-3">
              <button
                onClick={handleSapConfirmNo}
                className="px-5 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-400 transition-all duration-200"
              >
                No, Revert
              </button>
              <button
                onClick={handleSapConfirmOk}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg hover:from-amber-600 hover:to-orange-600 focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 transition-all duration-200 shadow-md"
              >
                Yes, Make Employee
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SAP Removal Confirmation Dialog (Employee -> User) */}
      {showSapRemovalConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowSapRemovalConfirm(false)} />

          <div className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden border-2 border-blue-400 dark:border-blue-500 animate-[fadeInScale_0.15s_ease-out]">

            {/* Warning Header */}
            <div className="bg-gradient-to-r from-blue-400 to-blue-600 px-6 py-4 flex items-center space-x-3">
              <div className="h-11 w-11 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white tracking-tight">Role Change Warning</h3>
                <p className="text-blue-100 text-xs mt-0.5">This action will modify the user's role</p>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                <p className="text-sm font-semibold text-blue-800 dark:text-blue-300 leading-relaxed">
                  ⚠️ Removing SAP Employee ID will automatically demote this user from{' '}
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 mx-0.5">Employee</span>
                  {' '}→{' '}
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300 mx-0.5">User</span>.
                </p>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400">
                Do you want to proceed? Pressing <span className="font-semibold text-gray-800 dark:text-gray-200">"No, Keep Employee"</span> will restore the SAP ID and keep the role as <span className="font-semibold">Employee</span>.
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 pb-5 flex justify-end space-x-3">
              <button
                onClick={handleSapRemovalConfirmNo}
                className="px-5 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-400 transition-all duration-200"
              >
                No, Keep Employee
              </button>
              <button
                onClick={handleSapRemovalConfirmOk}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg hover:from-blue-600 hover:to-blue-700 focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 transition-all duration-200 shadow-md"
              >
                Yes, Demote to User
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default UserManagementPage;